/**
 * Local, MIT-safe replacement for `@virtuoso.dev/message-list`.
 *
 * The original package is distributed under a commercial license and renders an
 * error watermark in production when no license key is present. This module
 * implements the subset of its API that the dust front-end actually consumes,
 * using a plain (non-virtualized) scroll container.
 *
 * Wired in through a Vite alias in `front-spa/vite.config.ts`, so no dust source
 * file needs to be modified.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

/* ------------------------------------------------------------------ *
 * Types
 * ------------------------------------------------------------------ */

export type ListScrollLocation = {
  listOffset: number;
  visibleListHeight: number;
  scrollHeight: number;
  bottomOffset: number;
  isAtBottom: boolean;
  lastVisibleItemIndex: number;
  lastItemBottomOffset: number;
};

type ScrollBehavior = "auto" | "instant" | "smooth";

type CustomScrollBehavior = (
  currentTop: number,
  targetTop: number
) => { easing: (t: number) => number; animationFrameCount: number };

type ItemLocation = {
  index: number | "LAST";
  align?: "start" | "end";
  behavior?: ScrollBehavior | CustomScrollBehavior;
  top?: number;
  forceBottomSpace?: number;
};

type ScrollToBottomParams = {
  data: unknown;
  scrollLocation: ListScrollLocation;
  scrollInProgress: boolean;
  context: unknown;
  atBottom: boolean;
};

type ScrollToBottomBehavior =
  | boolean
  | ScrollBehavior
  | ((
      params: ScrollToBottomParams
    ) => false | number | ScrollBehavior | ItemLocation | true | null | undefined);

export type VirtuosoMessageListMethods<T, C = null> = {
  data: {
    prepend: (items: T[]) => void;
    append: (items: T[], scrollToBottom?: ScrollToBottomBehavior) => void;
    replace: (items: T[], options?: Record<string, unknown>) => void;
    map: (
      mapper: (item: T, index: number) => T,
      autoscrollToBottomBehavior?: ScrollToBottomBehavior
    ) => void;
    mapWithAnchor: (
      mapper: (item: T, index: number) => T,
      anchorItemIndex: number
    ) => void;
    findAndDelete: (predicate: (item: T, index: number) => boolean) => void;
    findIndex: (predicate: (item: T, index: number) => boolean) => number;
    find: (predicate: (item: T, index: number) => boolean) => T | undefined;
    insert: (
      items: T[],
      offset: number,
      scrollToBottom?: ScrollToBottomBehavior
    ) => void;
    deleteRange: (offset: number, count: number) => void;
    batch: (fn: () => void, scrollToBottom?: ScrollToBottomBehavior) => void;
    get: () => T[];
    getCurrentlyRendered: () => T[];
    removeFromStart: (count: number) => void;
  };
  scrollToItem: (location: ItemLocation) => void;
  scrollIntoView: (target: any) => void;
  scrollerElement: () => HTMLElement | Window | null;
  getScrollLocation: () => ListScrollLocation;
  cancelSmoothScroll: () => void;
  height: (item: T) => number;
};

export type VirtuosoMessageListProps<T, C> = {
  data?: { data: T[]; scrollModifier?: any; initialLocation?: any };
  initialData?: T[];
  initialLocation?: any;
  ItemContent?: React.ComponentType<{
    index: number;
    data: T;
    context: C;
    prevData?: T;
    nextData?: T;
  }>;
  StickyFooter?: React.ComponentType<{ context: C }>;
  Header?: React.ComponentType<{ context: C }> | null;
  StickyHeader?: React.ComponentType<{ context: C }> | null;
  Footer?: React.ComponentType<{ context: C }> | null;
  EmptyPlaceholder?: React.ComponentType<{ context: C }> | null;
  context?: C;
  computeItemKey?: (args: { index: number; data: T; context: C }) => string;
  itemIdentity?: (item: T) => string;
  onScroll?: (location: ListScrollLocation) => void;
  onRenderedDataChange?: (data: T[]) => void;
  useWindowScroll?: boolean;
  increaseViewportBy?: number;
  enforceStickyFooterAtBottom?: boolean;
  shortSizeAlign?: "top" | "bottom" | "bottom-smooth";
  className?: string;
  style?: React.CSSProperties;
};

/* ------------------------------------------------------------------ *
 * Internal context + hooks
 * ------------------------------------------------------------------ */

const EMPTY_LOCATION: ListScrollLocation = {
  listOffset: 0,
  visibleListHeight: 0,
  scrollHeight: 0,
  bottomOffset: 0,
  isAtBottom: true,
  lastVisibleItemIndex: 0,
  lastItemBottomOffset: 0,
};

type InternalContextValue = {
  methods: VirtuosoMessageListMethods<any, any>;
  getLocation: () => ListScrollLocation;
  subscribe: (cb: () => void) => () => void;
  context: any;
};

const VirtuosoInternalContext = createContext<InternalContextValue | null>(
  null
);

const noopSubscribe = () => () => {};
const getEmptyLocation = () => EMPTY_LOCATION;

/**
 * No-op implementation, returned when a component that consumes the methods
 * renders outside of the list subtree (for example inside a portal). It lets
 * such a component degrade gracefully instead of crashing the whole view.
 */
function createNoopMethods<T, C>(): VirtuosoMessageListMethods<T, C> {
  const noop = () => {};
  return {
    data: {
      prepend: noop,
      append: noop,
      replace: noop,
      map: noop,
      mapWithAnchor: noop,
      findAndDelete: noop,
      findIndex: () => -1,
      find: () => undefined,
      insert: noop,
      deleteRange: noop,
      batch: (fn) => {
        fn();
      },
      get: () => [],
      getCurrentlyRendered: () => [],
      removeFromStart: noop,
    },
    scrollToItem: noop,
    scrollIntoView: noop,
    scrollerElement: () => null,
    getScrollLocation: () => EMPTY_LOCATION,
    cancelSmoothScroll: noop,
    height: () => 0,
  };
}

export function useVirtuosoMethods<T = any, C = any>(): VirtuosoMessageListMethods<
  T,
  C
> {
  const ctx = useContext(VirtuosoInternalContext);
  const fallbackRef = useRef<VirtuosoMessageListMethods<T, C> | null>(null);
  if (fallbackRef.current === null) {
    fallbackRef.current = createNoopMethods<T, C>();
  }
  return (ctx ? ctx.methods : fallbackRef.current) as VirtuosoMessageListMethods<
    T,
    C
  >;
}

export function useVirtuosoLocation(): ListScrollLocation {
  const ctx = useContext(VirtuosoInternalContext);
  return useSyncExternalStore(
    ctx ? ctx.subscribe : noopSubscribe,
    ctx ? ctx.getLocation : getEmptyLocation,
    getEmptyLocation
  );
}

/**
 * The commercial license wrapper. Kept as a no-op passthrough so that the
 * consumer code does not change and no license check runs.
 */
export function VirtuosoMessageListLicense(props: {
  licenseKey?: string;
  children?: React.ReactNode;
}) {
  return <>{props.children}</>;
}

/* ------------------------------------------------------------------ *
 * Component
 * ------------------------------------------------------------------ */

function VirtuosoMessageListInner<T, C>(
  props: VirtuosoMessageListProps<T, C>,
  ref: React.Ref<VirtuosoMessageListMethods<T, C>>
) {
  const {
    data: dataProp,
    initialData,
    ItemContent,
    StickyFooter,
    Header = null,
    StickyHeader = null,
    Footer = null,
    EmptyPlaceholder = null,
    context = null as unknown as C,
    computeItemKey,
    itemIdentity,
    onScroll,
    onRenderedDataChange,
    useWindowScroll = false,
    className,
    style,
  } = props;

  const isWindow = !!useWindowScroll;

  const initialList = dataProp?.data ?? initialData ?? [];

  const [items, setItems] = useState<T[]>(() => initialList.slice());
  const itemsRef = useRef<T[]>(items);
  itemsRef.current = items;

  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);

  const locationRef = useRef<ListScrollLocation>(EMPTY_LOCATION);
  const locationListenersRef = useRef<Set<() => void>>(new Set());
  const notifyLocation = useCallback(() => {
    locationListenersRef.current.forEach((cb) => cb());
  }, []);
  const subscribeLocation = useCallback((cb: () => void) => {
    locationListenersRef.current.add(cb);
    return () => {
      locationListenersRef.current.delete(cb);
    };
  }, []);
  const getLocation = useCallback(() => locationRef.current, []);

  const pendingScrollRef = useRef<ScrollToBottomBehavior | null>(null);
  const prependAnchorRef = useRef<number | null>(null);
  const anchorRef = useRef<{ index: number; offset: number } | null>(null);
  const rafRef = useRef<number | null>(null);
  const rafFlushRef = useRef<number | null>(null);

  /* -------------------------- scroll helpers -------------------------- */

  const readScroll = useCallback(() => {
    if (isWindow) {
      const list = listRef.current;
      const doc = document.documentElement;
      const listTop = list
        ? list.getBoundingClientRect().top + window.scrollY
        : 0;
      return {
        top: window.scrollY,
        viewport: window.innerHeight,
        content: Math.max(doc.scrollHeight, list ? list.scrollHeight : 0),
        listTop,
      };
    }
    const el = scrollerRef.current;
    if (!el) {
      return { top: 0, viewport: 0, content: 0, listTop: 0 };
    }
    return {
      top: el.scrollTop,
      viewport: el.clientHeight,
      content: el.scrollHeight,
      listTop: 0,
    };
  }, [isWindow]);

  const computeLocation = useCallback((): ListScrollLocation => {
    const { top, viewport, content, listTop } = readScroll();
    // listOffset is 0 at the top of the list and negative once scrolled down.
    const listOffset = -(top - listTop);
    // bottomOffset is 0 when the list bottom sits exactly at the viewport
    // bottom and grows as the user scrolls away from it.
    const bottomOffset = listTop + content - top - viewport;
    return {
      listOffset,
      visibleListHeight: viewport,
      scrollHeight: content,
      bottomOffset,
      isAtBottom: bottomOffset <= 1,
      lastVisibleItemIndex: Math.max(0, itemsRef.current.length - 1),
      lastItemBottomOffset: 0,
    };
  }, [readScroll]);

  const cancelSmoothScroll = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const applyScrollTop = useCallback(
    (value: number, behavior: ScrollBehavior = "instant") => {
      const target = Math.max(0, value);
      if (isWindow) {
        window.scrollTo({
          top: target,
          behavior: behavior === "smooth" ? "smooth" : ("instant" as any),
        });
        return;
      }
      const el = scrollerRef.current;
      if (!el) {
        return;
      }
      el.scrollTo({
        top: target,
        behavior: behavior === "smooth" ? "smooth" : ("instant" as any),
      });
    },
    [isWindow]
  );

  const setScrollTop = useCallback(
    (target: number, behavior?: ScrollBehavior | CustomScrollBehavior) => {
      const current = readScroll().top;

      if (typeof behavior === "function") {
        cancelSmoothScroll();
        const { easing, animationFrameCount } = behavior(current, target);
        const frames = Math.max(1, animationFrameCount);
        let frame = 0;
        const step = () => {
          frame += 1;
          const progress = Math.min(1, frame / frames);
          applyScrollTop(current + (target - current) * easing(progress));
          if (frame < frames) {
            rafRef.current = requestAnimationFrame(step);
          } else {
            applyScrollTop(target);
            rafRef.current = null;
          }
        };
        rafRef.current = requestAnimationFrame(step);
        return;
      }

      cancelSmoothScroll();
      applyScrollTop(target, behavior === "smooth" ? "smooth" : "instant");
    },
    [readScroll, cancelSmoothScroll, applyScrollTop]
  );

  const applyScrollToLocation = useCallback(
    (location: ItemLocation) => {
      if (typeof location.top === "number") {
        setScrollTop(location.top, location.behavior);
        return;
      }

      const { viewport, content, listTop } = readScroll();

      let target: number;
      if (location.index === "LAST") {
        target = listTop + content - viewport;
      } else {
        const list = listRef.current;
        const el = list
          ? list.querySelector<HTMLElement>(
              '[data-index="' + location.index + '"]'
            )
          : null;
        if (!el) {
          return;
        }
        const elTop = el.offsetTop;
        const elHeight = el.offsetHeight;
        target =
          location.align === "start"
            ? listTop + elTop
            : listTop + elTop + elHeight - viewport;
      }

      const max = Math.max(0, listTop + content - viewport);
      setScrollTop(Math.max(0, Math.min(target, max)), location.behavior);
    },
    [readScroll, setScrollTop]
  );

  /**
   * Resolves and applies whatever scroll request is queued.
   *
   * It runs from the post-render layout effect, so freshly appended items are
   * already in the DOM and measured, and from a requestAnimationFrame fallback
   * so that calls which do not change the data (a "scroll to bottom" button,
   * for instance) still take effect.
   */
  const flushPendingScroll = useCallback(() => {
    const pending = pendingScrollRef.current;
    if (pending === null || pending === undefined || pending === false) {
      pendingScrollRef.current = null;
      return;
    }
    pendingScrollRef.current = null;

    const location = computeLocation();
    let resolved: ItemLocation | null = null;

    if (typeof pending === "function") {
      const result = pending({
        data: itemsRef.current,
        scrollLocation: location,
        scrollInProgress: false,
        context,
        atBottom: location.isAtBottom,
      });
      if (result === false || result === null || result === undefined) {
        resolved = null;
      } else if (typeof result === "number") {
        resolved = { index: result, align: "end", behavior: "auto" };
      } else if (typeof result === "object") {
        resolved = result as ItemLocation;
      } else {
        resolved = {
          index: "LAST",
          align: "end",
          behavior: result === true ? "auto" : result,
        };
      }
    } else if (pending === true) {
      resolved = { index: "LAST", align: "end", behavior: "auto" };
    } else if (typeof pending === "object") {
      resolved = pending as ItemLocation;
    } else {
      resolved = {
        index: "LAST",
        align: "end",
        behavior: pending as ScrollBehavior,
      };
    }

    if (resolved) {
      applyScrollToLocation(resolved);
    }
  }, [computeLocation, applyScrollToLocation, context]);

  const scrollToItem = useCallback(
    (location: ItemLocation) => {
      pendingScrollRef.current = location;
      if (rafFlushRef.current === null) {
        rafFlushRef.current = requestAnimationFrame(() => {
          rafFlushRef.current = null;
          flushPendingScroll();
        });
      }
    },
    [flushPendingScroll]
  );

  /* ----------------------------- methods ----------------------------- */

  const methods = useMemo<VirtuosoMessageListMethods<T, C>>(() => {
    const commit = (next: T[]) => {
      itemsRef.current = next;
      setItems(next);
    };

    const queueScroll = (behavior?: ScrollToBottomBehavior) => {
      if (behavior === undefined) {
        return;
      }
      pendingScrollRef.current = behavior;
    };

    return {
      data: {
        get: () => itemsRef.current.slice(),
        getCurrentlyRendered: () => itemsRef.current.slice(),
        prepend: (newItems) => {
          prependAnchorRef.current = readScroll().content;
          commit([...newItems, ...itemsRef.current]);
        },
        append: (newItems, scrollToBottom) => {
          commit([...itemsRef.current, ...newItems]);
          queueScroll(scrollToBottom);
        },
        insert: (newItems, offset, scrollToBottom) => {
          const current = itemsRef.current;
          commit([
            ...current.slice(0, offset),
            ...newItems,
            ...current.slice(offset),
          ]);
          queueScroll(scrollToBottom);
        },
        replace: (newItems) => {
          commit(newItems.slice());
        },
        map: (mapper, autoscrollToBottomBehavior) => {
          commit(itemsRef.current.map((item, index) => mapper(item, index)));
          queueScroll(autoscrollToBottomBehavior);
        },
        mapWithAnchor: (mapper, anchorItemIndex) => {
          const list = listRef.current;
          const el = list
            ? list.querySelector<HTMLElement>(
                '[data-index="' + anchorItemIndex + '"]'
              )
            : null;
          anchorRef.current = el
            ? { index: anchorItemIndex, offset: el.offsetTop - readScroll().top }
            : null;
          commit(itemsRef.current.map((item, index) => mapper(item, index)));
        },
        findAndDelete: (predicate) => {
          commit(
            itemsRef.current.filter((item, index) => !predicate(item, index))
          );
        },
        findIndex: (predicate) =>
          itemsRef.current.findIndex((item, index) => predicate(item, index)),
        find: (predicate) =>
          itemsRef.current.find((item, index) => predicate(item, index)),
        deleteRange: (offset, count) => {
          const current = itemsRef.current;
          commit([...current.slice(0, offset), ...current.slice(offset + count)]);
        },
        batch: (fn, scrollToBottom) => {
          fn();
          queueScroll(scrollToBottom);
        },
        removeFromStart: (count) => {
          prependAnchorRef.current = readScroll().content;
          commit(itemsRef.current.slice(count));
        },
      },
      scrollToItem,
      scrollIntoView: (target: any) => {
        const list = listRef.current;
        if (!list || target === null || target === undefined) {
          return;
        }
        const index =
          typeof target === "object" ? target.index : Number(target);
        const el =
          target instanceof HTMLElement
            ? target
            : list.querySelector<HTMLElement>('[data-index="' + index + '"]');
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ block: "nearest" });
        }
      },
      scrollerElement: () => (isWindow ? window : scrollerRef.current),
      getScrollLocation: () => locationRef.current,
      cancelSmoothScroll,
      height: (item) => {
        const list = listRef.current;
        const index = itemsRef.current.indexOf(item);
        if (!list || index < 0) {
          return 0;
        }
        const el = list.querySelector<HTMLElement>('[data-index="' + index + '"]');
        return el ? el.offsetHeight : 0;
      },
    };
  }, [readScroll, scrollToItem, cancelSmoothScroll, isWindow]);

  useImperativeHandle(ref, () => methods, [methods]);

  /* --------------------- post-render scroll handling -------------------- */

  useLayoutEffect(() => {
    // Prepending (loading older messages) must not move the viewport.
    const prependAnchor = prependAnchorRef.current;
    if (prependAnchor !== null) {
      prependAnchorRef.current = null;
      const after = readScroll();
      const delta = after.content - prependAnchor;
      if (delta !== 0) {
        applyScrollTop(after.top + delta);
      }
    }

    // Anchored remap keeps a given item visually in place.
    const anchor = anchorRef.current;
    if (anchor) {
      anchorRef.current = null;
      const list = listRef.current;
      const el = list
        ? list.querySelector<HTMLElement>('[data-index="' + anchor.index + '"]')
        : null;
      if (el) {
        const after = readScroll();
        const delta = el.offsetTop - after.top - anchor.offset;
        if (delta !== 0) {
          applyScrollTop(after.top + delta);
        }
      }
    }

    // Explicit auto-scroll request coming from append/insert/map/batch.
    flushPendingScroll();
    onRenderedDataChange?.(itemsRef.current);
  }, [
    items,
    applyScrollTop,
    flushPendingScroll,
    onRenderedDataChange,
    readScroll,
  ]);

  /* ------------------------ data prop synchronisation ------------------- */

  const dataList = dataProp?.data;
  const scrollModifier = dataProp?.scrollModifier;
  const appliedDataRef = useRef<T[] | null>(null);

  useEffect(() => {
    if (!dataList || appliedDataRef.current === dataList) {
      return;
    }
    appliedDataRef.current = dataList;
    itemsRef.current = dataList.slice();
    setItems(dataList.slice());

    if (scrollModifier?.type === "item-location") {
      pendingScrollRef.current = {
        index: scrollModifier.location?.index ?? "LAST",
        align: scrollModifier.location?.align,
        behavior: scrollModifier.location?.behavior,
      };
    } else if (scrollModifier?.type === "auto-scroll-to-bottom") {
      pendingScrollRef.current = scrollModifier.autoScroll ?? true;
    }
  }, [dataList, scrollModifier]);

  /* ------------------------------ observers ---------------------------- */

  useEffect(() => {
    const target: any = isWindow ? window : scrollerRef.current;
    if (!target) {
      return;
    }
    const handler = () => {
      const location = computeLocation();
      locationRef.current = location;
      notifyLocation();
      onScroll?.(location);
    };
    target.addEventListener("scroll", handler, { passive: true });
    handler();
    return () => {
      target.removeEventListener("scroll", handler);
    };
  }, [isWindow, computeLocation, notifyLocation, onScroll]);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => {
      const location = computeLocation();
      locationRef.current = location;
      notifyLocation();
    });
    if (listRef.current) {
      observer.observe(listRef.current);
    }
    if (!isWindow && scrollerRef.current) {
      observer.observe(scrollerRef.current);
    }
    return () => observer.disconnect();
  }, [isWindow, computeLocation, notifyLocation]);

  /* ------------------------------- render ------------------------------ */

  const renderItem = (item: T, index: number) => {
    const key = computeItemKey
      ? computeItemKey({ index, data: item, context })
      : itemIdentity
        ? itemIdentity(item)
        : String(index);
    const Content = ItemContent;
    return (
      <div key={key} data-index={index}>
        {Content ? (
          <Content
            index={index}
            data={item}
            context={context}
            prevData={index > 0 ? items[index - 1] : undefined}
            nextData={index < items.length - 1 ? items[index + 1] : undefined}
          />
        ) : null}
      </div>
    );
  };

  const body =
    items.length === 0 && EmptyPlaceholder ? (
      <EmptyPlaceholder context={context} />
    ) : (
      items.map((item, index) => renderItem(item, index))
    );

  const content = (
    <>
      {Header ? <Header context={context} /> : null}
      {StickyHeader ? <StickyHeader context={context} /> : null}
      <div ref={listRef} style={{ position: "relative" }}>
        {body}
      </div>
      {Footer ? <Footer context={context} /> : null}
      {StickyFooter ? (
        <div style={{ position: "sticky", bottom: 0, zIndex: 1 }}>
          <StickyFooter context={context} />
        </div>
      ) : null}
    </>
  );

  return (
    <VirtuosoInternalContext.Provider
      value={{ methods, getLocation, subscribe: subscribeLocation, context }}
    >
      {isWindow ? (
        <div className={className} style={style}>
          {content}
        </div>
      ) : (
        <div
          ref={scrollerRef}
          className={className}
          style={{ overflowY: "auto", overflowAnchor: "none", ...style }}
        >
          {content}
        </div>
      )}
    </VirtuosoInternalContext.Provider>
  );
}

export const VirtuosoMessageList = React.forwardRef(
  VirtuosoMessageListInner as any
) as <T, C = null>(
  props: VirtuosoMessageListProps<T, C> & {
    ref?: React.Ref<VirtuosoMessageListMethods<T, C>>;
  }
) => React.ReactElement;

export default VirtuosoMessageList;
